import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaTwitter, FaLinkedinIn, FaInstagram, FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';

const FooterContainer = styled.footer`
  background: ${({ theme }) => theme.colors.gradient.primary};
  color: ${({ theme }) => theme.colors.light};
  padding: ${({ theme }) => theme.space['3xl']} 0 ${({ theme }) => theme.space.xl};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('/assets/images/pattern.svg');
    opacity: 0.03;
    pointer-events: none;
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.space.xl};
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.space.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterLogo = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: ${({ theme }) => theme.space.md};
  letter-spacing: -0.02em;
  
  span {
    color: ${({ theme }) => theme.colors.accent};
    font-family: ${({ theme }) => theme.fonts.accent};
    font-style: italic;
    margin-left: 2px;
  }
`;

const FooterDescription = styled.p`
  color: ${({ theme }) => theme.colors.grey[400]};
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: rgba(255, 255, 255, 0.1);
  color: ${({ theme }) => theme.colors.white};
  transition: all 0.3s ease;
  border: 1px solid transparent;
  
  &:hover {
    background-color: transparent;
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accent};
    transform: translateY(-3px);
  }
`;

const FooterHeading = styled.h4`
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  margin-bottom: ${({ theme }) => theme.space.lg};
  position: relative;
  font-family: ${({ theme }) => theme.fonts.heading};
  letter-spacing: 0.03em;
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -${({ theme }) => theme.space.xs};
    width: 40px;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
`;

const FooterLink = styled.li`
  margin-bottom: ${({ theme }) => theme.space.sm};
  
  a {
    color: ${({ theme }) => theme.colors.grey[300]};
    transition: all 0.3s ease;
    display: inline-block;
    position: relative;
    padding-left: ${({ theme }) => theme.space.sm};
    
    &::before {
      content: '›';
      position: absolute;
      left: 0;
      opacity: 0;
      transition: all 0.3s ease;
    }
    
    &:hover {
      color: ${({ theme }) => theme.colors.accent};
      transform: translateX(3px);
      
      &::before {
        opacity: 1;
      }
    }
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  color: ${({ theme }) => theme.colors.grey[300]};
  transition: all 0.3s ease;
  
  svg {
    color: ${({ theme }) => theme.colors.accent};
  }
  
  &:hover {
    transform: translateX(3px);
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: ${({ theme }) => theme.space.xl};
  margin-top: ${({ theme }) => theme.space.xl};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: ${({ theme }) => theme.colors.grey[500]};
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding-left: ${({ theme }) => theme.space.md};
  padding-right: ${({ theme }) => theme.space.md};
`;

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <FooterContainer>
      <FooterContent>
        <FooterColumn>
          <FooterLogo to="/">
            Data Digital
          </FooterLogo>
          <FooterDescription>
            Empowering businesses with innovative digital transformation strategies and cutting-edge data analytics solutions.
          </FooterDescription>
          <SocialLinks>
            <SocialLink href="#" target="_blank" rel="noopener noreferrer">
              <FaFacebookF />
            </SocialLink>
            <SocialLink href="#" target="_blank" rel="noopener noreferrer">
              <FaTwitter />
            </SocialLink>
            <SocialLink href="#" target="_blank" rel="noopener noreferrer">
              <FaLinkedinIn />
            </SocialLink>
            <SocialLink href="#" target="_blank" rel="noopener noreferrer">
              <FaInstagram />
            </SocialLink>
          </SocialLinks>
        </FooterColumn>
        
        <FooterColumn>
          <FooterHeading>Quick Links</FooterHeading>
          <FooterLinks>
            <FooterLink><Link to="/">Home</Link></FooterLink>
            <FooterLink><Link to="/about">About Us</Link></FooterLink>
            <FooterLink><Link to="/services">Services</Link></FooterLink>
            <FooterLink><Link to="/blog">Blog</Link></FooterLink>
            <FooterLink><Link to="/contact">Contact</Link></FooterLink>
          </FooterLinks>
        </FooterColumn>
        
        <FooterColumn>
          <FooterHeading>Our Services</FooterHeading>
          <FooterLinks>
            <FooterLink><Link to="/services/digital-marketing">Digital Marketing</Link></FooterLink>
            <FooterLink><Link to="/services/ai-analytics">AI-Powered Analytics</Link></FooterLink>
            <FooterLink><Link to="/services/business-intelligence">Business Intelligence</Link></FooterLink>
            <FooterLink><Link to="/services/strategic-consulting">Strategic Consulting</Link></FooterLink>
            <FooterLink><Link to="/services/social-media">Social Media Management</Link></FooterLink>
          </FooterLinks>
        </FooterColumn>
        
        <FooterColumn>
          <FooterHeading>Contact Us</FooterHeading>
          <ContactInfo>
            <ContactItem>
              <FaMapMarkerAlt />
              <span>Saguna More, Patna, Bihar</span>
            </ContactItem>
            <ContactItem>
              <FaPhone />
              <span>6203306234</span>
            </ContactItem>
            <ContactItem>
              <FaEnvelope />
              <span>connect@datadigital.in</span>
            </ContactItem>
          </ContactInfo>
        </FooterColumn>
      </FooterContent>
      
      <Copyright>
        &copy; {currentYear} Data Digital. All rights reserved. | <Link to="/privacy-policy" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</Link> | <Link to="/terms-of-service" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms of Service</Link>
      </Copyright>
    </FooterContainer>
  );
};

export default Footer;
